import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import RefreshIcon from '@mui/icons-material/Refresh'
import ErrorBanner from '../../components/shared/ErrorBanner'
import {
    CATEGORY_LABEL,
    audienceSummary,
    whenText,
} from '../../helpers/trainerNotifications'
import TrainerNotificationService from '../../services/trainerNotificationService'
import { errorMessage } from '../../services/axios/errorMessage'
import { SURFACE, TEXT, cardSx, pageSx } from '../../theme/tokens'
import {
    LifecycleChip,
    NotificationHeader,
    TagPill,
} from './notificationShared'

const PAGE_SIZE = 20
const COLUMNS = [
    'Notification',
    'Category',
    'Tag',
    'Audience',
    'Start',
    'End',
    'Status',
    'Created by',
    'Created',
]

const cellSx = { borderColor: SURFACE.border, typography: 'caption' }
const mutedCell = { ...cellSx, color: TEXT.secondary }

/**
 * Trainer dashboard notifications, newest first: drafts and published ones,
 * each with where it is in its life (Draft, Scheduled, Active, Expired).
 * A row opens its page (report, edit or delete a draft, deactivate).
 */
const TrainerNotifications = () => {
    const navigate = useNavigate()
    const [rows, setRows] = useState([])
    const [options, setOptions] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [page, setPage] = useState(0)

    const load = useCallback(async () => {
        setLoading(true)
        setError('')
        try {
            const res = await TrainerNotificationService.list()
            setRows(res?.data?.notifications ?? [])
        } catch (err) {
            setRows([])
            setError(errorMessage(err?.response))
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
        // Only to name regions and products in the audience column; the
        // codes are shown when it cannot be read.
        TrainerNotificationService.audienceOptions()
            .then((res) => setOptions(res?.data ?? null))
            .catch(() => setOptions(null))
    }, [load])

    const open = (n) =>
        navigate(
            `/trainer-notifications/${encodeURIComponent(n.notification_id)}`
        )

    const visible = rows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE)

    return (
        <Box sx={pageSx}>
            <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 3, md: 4 } }}>
                <NotificationHeader
                    title="Trainer Notifications"
                    subtitle="Notifications on the trainer dashboard's strip and Updates page. Times are IST."
                    actions={
                        <>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={load}
                                disabled={loading}
                            >
                                Refresh
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() =>
                                    navigate('/trainer-notifications/new')
                                }
                            >
                                New notification
                            </Button>
                        </>
                    }
                />

                {error && (
                    <Box sx={{ mb: 2 }}>
                        <ErrorBanner message={error} />
                    </Box>
                )}

                <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table size="small" sx={{ minWidth: 1100 }}>
                            <TableHead>
                                <TableRow>
                                    {COLUMNS.map((h) => (
                                        <TableCell key={h} sx={cellSx}>
                                            <Typography variant="overline">
                                                {h}
                                            </Typography>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={COLUMNS.length}
                                            sx={cellSx}
                                            align="center"
                                        >
                                            <CircularProgress size={20} />
                                        </TableCell>
                                    </TableRow>
                                ) : visible.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={COLUMNS.length}
                                            sx={cellSx}
                                            align="center"
                                        >
                                            <Typography
                                                sx={{
                                                    typography: 'body2',
                                                    color: TEXT.muted,
                                                    py: 3,
                                                }}
                                            >
                                                No trainer notifications yet.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    visible.map((n) => (
                                        <TableRow
                                            key={n.notification_id}
                                            hover
                                            role="link"
                                            tabIndex={0}
                                            aria-label={`Open ${
                                                n.title || 'notification'
                                            }`}
                                            onClick={() => open(n)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') open(n)
                                            }}
                                            sx={{ cursor: 'pointer' }}
                                            data-testid="trainer-notification-row"
                                        >
                                            <TableCell
                                                sx={{
                                                    ...cellSx,
                                                    maxWidth: 320,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        typography: 'body2',
                                                        fontWeight: 600,
                                                        color: TEXT.primary,
                                                    }}
                                                    noWrap
                                                >
                                                    {n.title || '(no name)'}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        typography: 'caption',
                                                        color: TEXT.muted,
                                                    }}
                                                    noWrap
                                                    title={n.message}
                                                >
                                                    {n.message || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={mutedCell}>
                                                {CATEGORY_LABEL[n.category] ??
                                                    n.category ??
                                                    '-'}
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                <TagPill tag={n.tag} />
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    ...mutedCell,
                                                    maxWidth: 260,
                                                }}
                                            >
                                                {audienceSummary(
                                                    n,
                                                    options
                                                ).join(' · ') || '-'}
                                            </TableCell>
                                            <TableCell sx={mutedCell}>
                                                {whenText(n.start_date)}
                                            </TableCell>
                                            <TableCell sx={mutedCell}>
                                                {whenText(n.end_date)}
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                <LifecycleChip
                                                    lifecycle={n.lifecycle}
                                                />
                                            </TableCell>
                                            <TableCell sx={mutedCell}>
                                                {n.created_by || '-'}
                                            </TableCell>
                                            <TableCell sx={mutedCell}>
                                                {whenText(n.created_on)}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {rows.length > PAGE_SIZE && (
                        <TablePagination
                            component="div"
                            count={rows.length}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={PAGE_SIZE}
                            rowsPerPageOptions={[PAGE_SIZE]}
                        />
                    )}
                </Paper>
            </Box>
        </Box>
    )
}

export default TrainerNotifications
